import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import "./ActivityFeed.css";

const POLL_INTERVAL_MS = 30000;
const TOAST_MS = 6000;

function isMale(user) {
  return String(user?.gender || "").toUpperCase().startsWith("M");
}

export function formatActivityTime(value, t) {
  if (!value) {
    return "";
  }

  const diff = Math.max(Date.now() - new Date(value).getTime(), 0);
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) {
    return t("activity.justNow");
  }
  if (minutes < 60) {
    return t("activity.minutesAgo", { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t("activity.hoursAgo", { count: hours });
  }
  return new Date(value).toLocaleDateString();
}

function eventTarget(event) {
  if (event.matchId && (event.type === "MESSAGE" || event.type === "ONLINE")) {
    return `/messages/${event.matchId}`;
  }
  return "/matches";
}

function ActivityFeed() {
  const { user, isAuthenticated } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const knownIds = useRef(null);
  const toastTimer = useRef(null);

  const enabled = isAuthenticated && isMale(user);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/api/activity", { params: { limit: 30 } });
      const list = response.data?.events ?? [];
      setEvents(list);
      setUnseenCount(response.data?.unseenCount ?? 0);

      if (knownIds.current === null) {
        knownIds.current = new Set(list.map((item) => item.id));
        return;
      }

      const fresh = list.find((item) => !knownIds.current.has(item.id) && !item.seen);
      list.forEach((item) => knownIds.current.add(item.id));
      if (fresh) {
        setToast(fresh);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
      }
    } catch (err) {
      // Activity is non-critical; ignore failures.
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      knownIds.current = null;
      setEvents([]);
      setUnseenCount(0);
      return undefined;
    }

    load();
    const timer = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(toastTimer.current);
    };
  }, [enabled, load]);

  useEffect(() => {
    document.body.classList.toggle("has-activity-feed", enabled);
    return () => document.body.classList.remove("has-activity-feed");
  }, [enabled]);

  const markSeen = useCallback(async () => {
    setUnseenCount(0);
    setEvents((prev) => prev.map((item) => ({ ...item, seen: true })));
    try {
      await api.post("/api/activity/seen");
    } catch (err) {
      // ignore
    }
  }, []);

  if (!enabled) {
    return null;
  }

  function openEvent(event) {
    setPanelOpen(false);
    setToast(null);
    markSeen();
    navigate(eventTarget(event));
  }

  function togglePanel() {
    const next = !panelOpen;
    setPanelOpen(next);
    if (next && unseenCount > 0) {
      markSeen();
    }
  }

  function renderItem(event) {
    return (
      <button
        type="button"
        key={event.id}
        className={`activity-item${event.seen ? "" : " activity-item-unseen"}`}
        onClick={() => openEvent(event)}
      >
        <span className="activity-avatar">
          {event.actorPhotoUrl ? (
            <img src={event.actorPhotoUrl} alt="" loading="lazy" />
          ) : (
            <span className="activity-avatar-fallback">{(event.actorName || "?").charAt(0)}</span>
          )}
          <span className={`activity-dot${event.actorOnline ? " activity-dot-online" : ""}`} />
        </span>
        <span className="activity-body">
          <span className="activity-name">{event.actorName}</span>
          <span className="activity-text">{t(`activity.types.${event.type}`)}</span>
          <span className="activity-meta">
            {formatActivityTime(event.occurredAt, t)} ·{" "}
            {event.actorOnline ? t("activity.online") : t("activity.offline")}
          </span>
        </span>
      </button>
    );
  }

  const list = events.length === 0
    ? <div className="activity-empty">{t("activity.empty")}</div>
    : events.map(renderItem);

  return (
    <>
      <aside className="activity-column" aria-label={t("activity.title")}>
        <h3 className="activity-title">{t("activity.title")}</h3>
        <div className="activity-list">{list}</div>
      </aside>

      <button
        type="button"
        className="activity-fab"
        onClick={togglePanel}
        aria-label={t("activity.title")}
      >
        <span aria-hidden="true">🔔</span>
        {unseenCount > 0 && <span className="activity-badge">{unseenCount > 9 ? "9+" : unseenCount}</span>}
      </button>

      {panelOpen && (
        <div className="activity-panel-overlay" onClick={() => setPanelOpen(false)}>
          <div className="activity-panel" onClick={(e) => e.stopPropagation()}>
            <div className="activity-panel-header">
              <h3 className="activity-title">{t("activity.title")}</h3>
              <button type="button" className="activity-close" onClick={() => setPanelOpen(false)}>
                ×
              </button>
            </div>
            <div className="activity-list">{list}</div>
          </div>
        </div>
      )}

      {toast && !panelOpen && (
        <div className="activity-toast">
          {renderItem(toast)}
        </div>
      )}
    </>
  );
}

export default ActivityFeed;
